import { accountGeneration } from '../../../lib/accountStorage';
import { ACCOUNT_CHANGED, markDoseGroup, type GroupMarkResult } from '../../../lib/doseGroupMark';
import { useHabitStore } from '../../../stores/habitStore';
import { useSupplementStore } from '../../../stores/supplementStore';
import type { Habit } from '../../../types';
import { canCompleteHabitOn, habitSlots } from '../../../utils/habitProgress';
import { parseLocalDate } from '../../../utils/date';
import { toLocalYMD } from '../../../utils/hydration';
import { markOf, TIMES_OF_DAY, type DoseMark, type TimeOfDay } from '../../../utils/intakeSchedule';
import { compartmentComplete, compartmentOn, linkedSupplementsHabit, supplementsOpId } from '../../../utils/supplementsLink';

/**
 * ⭐ Mark a dose — and keep the supplements habit in step: ONE operation, shared (owner,
 * 2026-10-23 — step 6 of the Supplements pass, the analogue of Nutrition's `ateAsPlanned`).
 * Every place that marks one of today's doses goes through here: Today's list (the composer's
 * `onMark`, and its "✓ All N taken" — `markGroupLinked`), the Home widget's tile, and the habit's
 * prompt ("Mark all taken").
 *
 *   · After a mark lands, if that dose's time of day now has EVERY due dose recorded and at
 *     least one taken, the linked habit's interval for it is ticked — CONFIRMED (no prompt),
 *     under the link's own operation id (`supplementsOpId`: deterministic per day and time of
 *     day), so it is idempotent: never twice, whoever (or whichever device) gets there first.
 *   · If a later Undo (or a change) leaves that time of day short of that, and the interval's
 *     tick is the LINK's (its completion carries that operation id), the tick is taken back —
 *     never a tick the person made by hand (another id, or none).
 *   · Both are applied to the cloud's habit (the habit store's `completeLinked` /
 *     `uncompleteByOp` — changes, not copies), and respect the habit's own day rule.
 *
 * Plain functions over the stores' `getState()` — no React; each call reads the stores at the
 * moment it runs, never a render's copy.
 *
 * Try again = call again with the SAME arguments (the result carries `opId` and `account`): the
 * mark is a no-op when it is already there, and the tick re-saves under the same operation id.
 *
 * ⭐⚠️ ONE ACCOUNT, as `ateAsPlanned`. `account` = `accountGeneration()` when the press happened
 * (default: now). A call for an account that has since signed out does nothing and says so
 * (`ACCOUNT_CHANGED`), and an operation stops at the first await after the account changes: it
 * never marks, ticks or unticks for the next one.
 *
 * ⚠️ THE CALLER'S DUTIES (Today and Home do them): one call per dose at a time; only today's doses
 * of a day whose record HAS LOADED (`isInitialized`, the store's `todayDate` = today).
 *
 * XP: a habit tick earns what any habit tick earns (the habit store mints it); Supplements' own
 * adherence XP is unchanged. Medications are not linked (their group mark: `medicationStore.markGroup`).
 */

export { ACCOUNT_CHANGED };

/** What happened to the habit after a mark. */
export interface LinkFollow {
  ok: boolean;
  /** Words for a tick (or untick) that didn't save — the mark itself DID. */
  error?: string;
  /** This call ticked (`'ticked'`) or took back (`'unticked'`) the link's interval. */
  did?: 'ticked' | 'unticked';
  /** The operation id of the link's tick for that time of day (Try again reuses it). */
  opId?: string;
}

export interface MarkLinkedResult extends LinkFollow {
  /** The mark itself saved (the dose reads as marked). */
  marked: boolean;
  /** The time of day the dose falls in (null: no such dose today). */
  part: TimeOfDay | null;
  /** The account it ran for — pass it back to Try again. */
  account: number;
}

const LABEL: Record<TimeOfDay, string> = Object.fromEntries(TIMES_OF_DAY.map(t => [t.id, t.label])) as Record<TimeOfDay, string>;

/** The completion the link made on `h` for that operation (bonus records aside). */
const linkTick = (h: Habit, opId: string) => (h.completions ?? []).some(c => c.opId === opId && !c.bonus);

/**
 * Make the linked habit agree with the record for one time of day of TODAY: tick its interval
 * when the time of day is complete (every due dose recorded, at least one taken), take the
 * link's own tick back when it isn't. Nothing to do → ok. Re-running is safe.
 */
export async function followCompartment({ part, account = accountGeneration() }: { part: TimeOfDay; account?: number }): Promise<LinkFollow> {
  if (account !== accountGeneration()) return { ok: false, error: ACCOUNT_CHANGED };
  const sup = useSupplementStore.getState();
  const date = toLocalYMD(new Date());
  // Only the day the record holds, loaded, for the account the habits are loaded for.
  if (!sup.isInitialized || sup.todayDate !== date) return { ok: true };
  const habits = useHabitStore.getState();
  if (!habits.initializedUserId || habits.initializedUserId !== sup.initializedUserId) return { ok: true };
  const habit = linkedSupplementsHabit(habits.habits);
  if (!habit) return { ok: true };
  // The interval for this time of day among the intervals it has today; a habit with none (no
  // intervals at all) follows the whole day.
  const slots = habitSlots(habit, date);
  const whole = !slots;
  if (slots && !slots.some(s => s.id === part)) return { ok: true };
  const key = whole ? 'day' : part;
  const opId = supplementsOpId(date, key);
  const state = compartmentOn(sup.stack, sup.todayTakenIds, date, sup.dayTimes, key);
  const what = whole ? habit.name : `${habit.name} · ${LABEL[part]}`;

  if (compartmentComplete(state)) {
    if (!canCompleteHabitOn(habit, parseLocalDate(date), new Date())) return { ok: true, opId };
    const t = await useHabitStore.getState().completeLinked(habit.id, date, whole ? null : part, opId);
    if (account !== accountGeneration()) return { ok: false, error: ACCOUNT_CHANGED, opId };
    return t.ok
      ? { ok: true, opId, ...(t.created ? { did: 'ticked' as const } : {}) }
      : { ok: false, opId, error: `Ticking “${what}” didn’t save — Try again ticks it; nothing is marked twice.` };
  }
  // Short of complete: only the link's own tick goes (a tick made by hand stays).
  if (!linkTick(habit, opId)) return { ok: true, opId };
  const ok = await useHabitStore.getState().uncompleteByOp(habit.id, opId);
  if (account !== accountGeneration()) return { ok: false, error: ACCOUNT_CHANGED, opId };
  return ok ? { ok: true, opId, did: 'unticked' } : { ok: false, opId, error: `Unticking “${what}” didn’t save — Try again.` };
}

/**
 * Mark one of today's doses taken, skipped or neither (the store's `markDose`), THEN keep the
 * linked habit in step for that dose's time of day (`followCompartment`). The habit follows the
 * record as it reads after the write — a refused mark too (the record is put back, so a tick
 * decided on it meanwhile is taken back).
 */
export async function markDoseLinked({ key, mark, account = accountGeneration() }: { key: string; mark: DoseMark; account?: number }): Promise<MarkLinkedResult> {
  if (account !== accountGeneration()) return { ok: false, marked: false, part: null, account, error: ACCOUNT_CHANGED };
  const s0 = useSupplementStore.getState();
  const part = s0.getTodayDoses().find(d => d.key === key)?.timeOfDay ?? null;
  let marked = false;
  try { marked = await s0.markDose(key, mark); } catch { marked = false; }
  // Signed out (or another account in) while it saved: no tick, no untick for the next one.
  if (account !== accountGeneration()) return { ok: false, marked, part, account, error: ACCOUNT_CHANGED };
  if (!part) return { ok: marked, marked, part, account };
  const f = await followCompartment({ part, account });
  // A habit write that didn't save says the dose IS recorded first (its own row shows that too).
  const name = s0.stack.find(i => i.id === s0.getTodayDoses().find(d => d.key === key)?.itemId)?.name;
  const error = f.error && f.error !== ACCOUNT_CHANGED && marked && name
    ? `${name} is ${mark === null ? 'back to not recorded' : `marked ${mark}`}. ${f.error}` : f.error;
  return { ...f, ...(error ? { error } : {}), ok: marked && f.ok, marked, part, account };
}

/** A dose's item, by name, for the words. */
const nameOf = (itemId: string) => useSupplementStore.getState().stack.find(i => i.id === itemId)?.name ?? 'a dose';
/** Today's "✓ All N taken" and the prompt's "Mark all taken": ONE loop (lib/doseGroupMark), one account. */
const markGroupOf = (keys: readonly string[], account: number) =>
  markDoseGroup(keys, { read: () => useSupplementStore.getState(), stillHere: () => account === accountGeneration(), noun: 'supplements' });

export interface MarkAllResult {
  ok: boolean;
  error?: string;
  /** Every open dose is now marked taken (a refused tick after that says so in `error`). */
  marked: boolean;
  /** The habit's interval was ticked by this call. */
  ticked: boolean;
  opId: string;
  account: number;
}

/**
 * ⭐ The prompt's "Mark all taken": every OPEN dose of that time of day (or of the day, `'day'`)
 * marked taken — the SAME loop as Today's "✓ All N taken" (lib/doseGroupMark: one after another,
 * the record read again before each, a dose recorded meanwhile left as it is) — then the interval
 * ticked CONFIRMED under the link's operation id. A refused mark doesn't stop the rest, and the
 * words are the same: "1 of 2 saved · Try again for Probiotic"; nothing is ticked until every one
 * saved. Try again marks what is still open. Today only (the record writes today's row).
 */
export async function markAllTaken({ habitId, part, slotId, account = accountGeneration() }: {
  habitId: string; part: TimeOfDay | 'day'; slotId: string | null; account?: number;
}): Promise<MarkAllResult> {
  const date = toLocalYMD(new Date());
  const opId = supplementsOpId(date, part);
  const answer = (r: Partial<MarkAllResult>): MarkAllResult => ({ ok: false, marked: false, ticked: false, opId, account, ...r });
  if (account !== accountGeneration()) return answer({ error: ACCOUNT_CHANGED });
  const s0 = useSupplementStore.getState();
  if (!s0.isInitialized || s0.todayDate !== date) return answer({ error: 'Today’s supplements haven’t loaded yet — try again in a moment.' });
  const open = compartmentOn(s0.stack, s0.todayTakenIds, date, s0.dayTimes, part).doses
    .filter(d => markOf(s0.todayTakenIds, d.key) === null).map(d => d.key);
  const r = await markGroupOf(open, account);
  if (account !== accountGeneration()) return answer({ error: ACCOUNT_CHANGED });
  if (!r.ok) return answer({ error: r.error });
  const t = await useHabitStore.getState().completeLinked(habitId, date, slotId, opId);
  if (account !== accountGeneration()) return answer({ marked: true, error: ACCOUNT_CHANGED });
  return t.ok
    ? answer({ ok: true, marked: true, ticked: t.created })
    : answer({ marked: true, error: 'Your doses are marked taken, but ticking the habit didn’t save — Try again ticks it; nothing is marked twice.' });
}

export interface MarkGroupLinkedResult extends GroupMarkResult {
  /** The habit, followed ONCE after the last mark (for each time of day the doses fall in). */
  habit: LinkFollow;
  /** The account it ran for — pass it back to Try again. */
  account: number;
}

/**
 * Several follows as one: ok when all were; the first error; "ticked" / "unticked" when any did.
 * No `opId`: there is one per time of day, and Try again doesn't need them (it calls again with the
 * same keys — each tick's id is fixed by the day and its time of day, `supplementsOpId`).
 */
function mergeFollows(fs: LinkFollow[]): LinkFollow {
  if (fs.length === 1) return fs[0];
  const error = fs.find(f => f.error)?.error;
  const did = fs.some(f => f.did === 'ticked') ? 'ticked' : fs.some(f => f.did === 'unticked') ? 'unticked' : undefined;
  return { ok: fs.every(f => f.ok), ...(error ? { error } : {}), ...(did ? { did } : {}) };
}

/**
 * ⭐ Today's "✓ All 4 taken" (Supplements polish round, 2026-10-24): each still-open dose of
 * `keys` marked taken, one after another, the record READ AGAIN before each (lib/doseGroupMark
 * `markDoseGroup` — a dose recorded meanwhile is left as it is; a refused one doesn't stop the
 * rest) — THEN the linked habit followed ONCE, at the end, for the time of day the doses fall in
 * (`followCompartment`: ticked when it is now complete; never once per dose).
 *
 * Partial failure is said as it is: "2 of 4 saved · Try again for Creatine · Omega-3"; Try again
 * = the SAME keys (and `account`) — only what is still open is marked, the tick reuses its id.
 * A tick that didn't save says the doses ARE saved first.
 */
export async function markGroupLinked({ keys, account = accountGeneration() }: { keys: string[]; account?: number }): Promise<MarkGroupLinkedResult> {
  const stopped = (r: GroupMarkResult): MarkGroupLinkedResult =>
    ({ ...r, ok: false, error: ACCOUNT_CHANGED, habit: { ok: false, error: ACCOUNT_CHANGED }, account });
  if (account !== accountGeneration()) return stopped({ ok: false, saved: [], already: [], failed: [...new Set(keys)] });
  // The times of day the doses fall in, in the day's order — read before anything is marked.
  const today = useSupplementStore.getState().getTodayDoses();
  const parts = TIMES_OF_DAY.map(t => t.id).filter(p => today.some(d => d.timeOfDay === p && keys.includes(d.key)));
  const r = await markGroupOf(keys, account);
  if (account !== accountGeneration()) return stopped(r);
  // ONCE, at the end: the habit follows the record as it reads now (a refused mark too).
  const follows: LinkFollow[] = [];
  for (const part of parts) {
    follows.push(await followCompartment({ part, account }));
    if (account !== accountGeneration()) return stopped(r);
  }
  const habit = follows.length ? mergeFollows(follows) : { ok: true };
  // A tick that didn't save: the doses ARE saved, and the words say that first — the TAKEN ones
  // (one skipped elsewhere meanwhile is never "saved" as taken).
  const ids = useSupplementStore.getState().todayTakenIds;
  const taken = [...r.saved, ...r.already.filter(k => markOf(ids, k) === 'taken')];
  const one = taken.length === 1 ? today.find(d => d.key === taken[0]) : undefined;
  const lead = !taken.length ? '' : one ? `${nameOf(one.itemId)} is saved. ` : `All ${taken.length} are saved. `;
  const habitWords = !habit.error ? undefined : r.failed.length ? habit.error : `${lead}${habit.error}`;
  const error = [r.error, habitWords].filter(Boolean).join('. ') || undefined;
  return { ...r, ok: r.ok && habit.ok, ...(error ? { error } : {}), habit, account };
}
